import { useState } from "react";
import { ChevronDown, Copy, Link2, Pencil, Send, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { placeOf, shipmentMessage, stepLabel, trackUrl, when } from "@/lib/shipments";
import { clientWaLink, copyText } from "@/lib/proposal";
import { ShipmentForm } from "./ShipmentForm";
import { EventForm } from "./EventForm";

const btn = "flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-zinc-100 transition-colors hover:bg-white/10";

// Uma entrega no painel: etapas, aviso no WhatsApp, link, edição e exclusão.
export const ShipmentCard = ({ s, onChange, onRemove, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const events = Array.isArray(s.events) ? s.events : [];
  const last = events[events.length - 1];
  const call = async (fn, ok) => {
    setBusy(true);
    try {
      const { data } = await fn();
      onChange(data);
      if (ok) toast.success(ok);
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const notify = (ev = last) => window.open(clientWaLink(s.client_phone, shipmentMessage(s, ev)), "_blank");
  const addEvent = (f) => call(() => api.post(`/admin/shipments/${s.id}/events`, f), "Etapa salva. Avise o cliente no WhatsApp.");
  const save = (f) => call(() => api.put(`/admin/shipments/${s.id}`, f), "Entrega atualizada").then((ok) => ok && setEditing(false));

  return (
    <li data-testid={`shipment-${s.id}`} className="rounded-2xl border border-white/10">
      <button type="button" data-testid={`shipment-toggle-${s.id}`} onClick={() => setOpen((v) => !v)} className="flex w-full items-start justify-between gap-3 p-4 text-left">
        <div className="min-w-0">
          <p className="font-semibold text-white">{s.client_name} <span className="mono ml-1 text-xs font-normal text-zinc-400">{s.code}</span></p>
          <p className="mt-0.5 truncate text-sm text-zinc-300">{s.item}</p>
          <p data-testid={`shipment-status-${s.id}`} className="mt-1 text-xs text-zinc-400"><strong className="text-white">{stepLabel(last?.step)}</strong>{placeOf(last) ? ` · ${placeOf(last)}` : ""} · {when(s.updated_at)}</p>
        </div>
        <ChevronDown className={`mt-1 h-4 w-4 shrink-0 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-4 border-t border-white/10 p-4">
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid={`shipment-notify-${s.id}`} onClick={() => notify()} className={`${btn} border-[#25D366]/50 text-[#25D366]`}><Send className="h-3.5 w-3.5" /> Avisar no WhatsApp</button>
            <button type="button" data-testid={`shipment-copy-link-${s.id}`} onClick={() => copyText(trackUrl(s.code)).then(() => toast.success("Link copiado"))} className={btn}><Link2 className="h-3.5 w-3.5" /> Copiar link</button>
            <button type="button" data-testid={`shipment-copy-msg-${s.id}`} onClick={() => copyText(shipmentMessage(s, last)).then(() => toast.success("Mensagem copiada"))} className={btn}><Copy className="h-3.5 w-3.5" /> Copiar mensagem</button>
            <button type="button" data-testid={`shipment-edit-${s.id}`} onClick={() => setEditing((v) => !v)} className={btn}><Pencil className="h-3.5 w-3.5" /> Editar dados</button>
            <button type="button" data-testid={`shipment-delete-${s.id}`} onClick={() => onRemove(s)} className={`${btn} text-red-300`}><Trash2 className="h-3.5 w-3.5" /> Apagar</button>
          </div>
          {editing && <ShipmentForm initial={{ client_name: s.client_name, client_phone: s.client_phone, item: s.item, tracking_code: s.tracking_code }} busy={busy} submitLabel="Salvar dados" onSubmit={save} onCancel={() => setEditing(false)} testid={`shipment-edit-form-${s.id}`} />}
          <EventForm id={s.id} current={last?.step} busy={busy} onAdd={addEvent} />
          <ol data-testid={`shipment-events-${s.id}`} className="space-y-2">
            {events.map((ev, i) => ({ ev, i })).reverse().map(({ ev, i }) => (
              <li key={`${ev.at}-${i}`} className="flex items-start justify-between gap-3 rounded-xl bg-white/[0.04] px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="text-white"><strong>{stepLabel(ev.step)}</strong>{placeOf(ev) ? ` · ${placeOf(ev)}` : ""}</p>
                  {ev.note && <p className="text-zinc-300">{ev.note}</p>}
                  <p className="text-xs text-zinc-400">{when(ev.at)}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button type="button" data-testid={`shipment-event-notify-${s.id}-${i}`} aria-label="Avisar esta etapa no WhatsApp" onClick={() => notify(ev)} className="rounded-full p-1.5 text-[#25D366] hover:bg-white/10"><Send className="h-3.5 w-3.5" /></button>
                  {events.length > 1 && <button type="button" data-testid={`shipment-event-delete-${s.id}-${i}`} aria-label="Apagar etapa" onClick={() => call(() => api.delete(`/admin/shipments/${s.id}/events/${i}`), "Etapa apagada")} className="rounded-full p-1.5 text-zinc-400 hover:bg-white/10 hover:text-red-300"><X className="h-3.5 w-3.5" /></button>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </li>
  );
};
